


























































































































































































































import { RestClient } from '@/features/core/rest-client/rest-client';
import { TreeNodeType } from '@/types/iot-portal';
import { getGenericNotificationApiSaveErrorMessage } from '@/features/core/util/generic-notification-api-save-error-message';
import { getRxjsHttpErrorResponseBody, getRxjsHttpErrorStatus } from '@/features/core/util/rxjs-http-error-response';
import {
  EditGenericNotificationApiInput,
  GenericNotificationApiAuthenticationType,
} from '@/types/generic-notification-api.model';
import { defineComponent } from '@vue/composition-api';
import type { MethodOptions } from '@vue/composition-api';
import { isString } from 'lodash';
import { EMPTY, lastValueFrom } from 'rxjs';
import { mapActions } from 'vuex';
import { AppCustomerGenericNotificationApiSettingsViewQuery_customers } from './__generated__/AppCustomerGenericNotificationApiSettingsViewQuery';
import {
  TreeNodeQueryForGenericNotificationApi,
  TreeNodeQueryForGenericNotificationApi_treeNodes,
  TreeNodeQueryForGenericNotificationApi_treeNodes_items,
} from './__generated__/TreeNodeQueryForGenericNotificationApi';
import treeNodeQueryForGenericNotificationApi from './treeNodeQuery.gql';
import query from './view.gql';

interface GenericNotificationApiProps {
  customerId: string;
  thirdpartyConfigId: string;
}

interface AuthenticationTypeOption {
  value: GenericNotificationApiAuthenticationType;
  label: string;
}

interface GenericNotificationApiConfig extends EditGenericNotificationApiInput {
  treeNodes?: TreeNodeQueryForGenericNotificationApi_treeNodes | null;
}

interface GenericNotificationApiData {
  customers?: AppCustomerGenericNotificationApiSettingsViewQuery_customers;
  config: GenericNotificationApiConfig;
  client?: RestClient;
  partnerName: string;
  loading: boolean;
  authFieldsResetKey: number;
}

export default defineComponent<
  GenericNotificationApiProps,
  Record<string, unknown>,
  GenericNotificationApiData,
  {
    rootIds: () => string[];
    consumptionTreeNodeTypes: () => TreeNodeType[];
    selectableTreeNodeTypes: () => TreeNodeType[];
  },
  MethodOptions
>({
  inject: ['GENERIC_NOTIFICATION_API_REST_CLIENT'],
  props: {
    customerId: { type: String, required: true },
    thirdpartyConfigId: { type: String, required: true },
  },
  data(): GenericNotificationApiData {
    return {
      customers: undefined,
      config: {} as EditGenericNotificationApiInput,
      client: undefined as RestClient | undefined,
      partnerName: 'Generic Notification API',
      loading: true,
      authFieldsResetKey: 0,
    };
  },
  computed: {
    rootIds() {
      return this.customers === undefined ? [] : [this.customers.first.rootDirectory.id];
    },
    consumptionTreeNodeTypes() {
      return [TreeNodeType.Property, TreeNodeType.Directory, TreeNodeType.RootDirectory];
    },
    selectableTreeNodeTypes() {
      return [TreeNodeType.Property, TreeNodeType.RootDirectory, TreeNodeType.Directory];
    },
  },
  created() {
    this.client = this.GENERIC_NOTIFICATION_API_REST_CLIENT as unknown as RestClient;
  },
  apollo: {
    customers: {
      query,
      fetchPolicy: 'cache-and-network',
      variables() {
        return { customerId: this.customerId };
      },
      result() {
        // Execute your logic after the query has returned something
        this.loadData();
      },
    },
  },
  methods: {
    ...mapActions(['ADD_TOAST_MESSAGES']),
    getDropdownOptions(): AuthenticationTypeOption[] {
      return [
        { value: 'oauth_client_credentials', label: 'Client Credentials' },
        { value: 'basic_auth', label: 'Username / Password (Basic Auth)' },
        { value: 'pat', label: 'Personal Access Token (PAT)' },
      ];
    },
    reduceOptionToValue(option: AuthenticationTypeOption): GenericNotificationApiAuthenticationType {
      return option.value;
    },
    isClientCredentials(formData: EditGenericNotificationApiInput): boolean {
      return formData.authenticationType === 'oauth_client_credentials';
    },
    isBasicAuth(formData: EditGenericNotificationApiInput): boolean {
      return formData.authenticationType === 'basic_auth';
    },
    isPersonalAccessToken(formData: EditGenericNotificationApiInput): boolean {
      return formData.authenticationType === 'pat';
    },
    resetAuthenticationFields(
      formData: EditGenericNotificationApiInput,
      authenticationType: GenericNotificationApiAuthenticationType,
    ): void {
      if (authenticationType !== 'oauth_client_credentials') {
        this.clearAuthenticationField(formData, 'vendorAuthorizationEndpoint');
        this.clearAuthenticationField(formData, 'vendorClientId');
        this.clearAuthenticationField(formData, 'vendorClientSecret');
        this.clearAuthenticationField(formData, 'vendorScope');
      }

      if (authenticationType !== 'basic_auth') {
        this.clearAuthenticationField(formData, 'vendorUsername');
        this.clearAuthenticationField(formData, 'vendorPassword');
      }

      if (authenticationType !== 'pat') {
        this.clearAuthenticationField(formData, 'vendorPersonalAccessToken');
      }
      // ui-form-field keeps its own value holder, so remount auth fields after clearing config values.
      this.authFieldsResetKey += 1;
    },
    clearAuthenticationField(
      formData: EditGenericNotificationApiInput,
      field: keyof Pick<
        EditGenericNotificationApiInput,
        | 'vendorAuthorizationEndpoint'
        | 'vendorClientId'
        | 'vendorClientSecret'
        | 'vendorScope'
        | 'vendorPersonalAccessToken'
        | 'vendorUsername'
        | 'vendorPassword'
      >,
    ): void {
      formData[field] = null;
      this.config[field] = null;
    },
    isMandatoryFieldsAdded(formData: EditGenericNotificationApiInput): boolean {
      const hasBaseFields =
        this.isNonEmptyString(formData.label) &&
        this.isValidEndpoint(formData.vendorEndpoint) &&
        this.isNonEmptyString(formData.authenticationType) &&
        Boolean(formData.heatingSystem) &&
        Boolean(formData.consent) &&
        Boolean(formData.treeNodeIds?.length);

      if (!hasBaseFields) {
        return false;
      }

      return this.validateAuthenticationFields(formData);
    },
    validateAuthenticationFields(formData: EditGenericNotificationApiInput): boolean {
      if (this.isClientCredentials(formData)) {
        return (
          this.isValidEndpoint(formData.vendorAuthorizationEndpoint) &&
          this.isNonEmptyString(formData.vendorClientId) &&
          this.isNonEmptyString(formData.vendorClientSecret)
        );
      }

      if (this.isBasicAuth(formData)) {
        return this.isNonEmptyString(formData.vendorUsername) && this.isNonEmptyString(formData.vendorPassword);
      }

      if (this.isPersonalAccessToken(formData)) {
        return this.isNonEmptyString(formData.vendorPersonalAccessToken);
      }

      return false;
    },
    getAuthenticationPayload(input: EditGenericNotificationApiInput): EditGenericNotificationApiInput {
      if (this.isClientCredentials(input)) {
        return {
          vendorAuthorizationEndpoint: input.vendorAuthorizationEndpoint,
          vendorClientId: input.vendorClientId,
          vendorClientSecret: input.vendorClientSecret,
          ...(this.isNonEmptyString(input.vendorScope) ? { vendorScope: input.vendorScope } : {}),
        };
      }

      if (this.isBasicAuth(input)) {
        return {
          vendorUsername: input.vendorUsername,
          vendorPassword: input.vendorPassword,
        };
      }

      if (this.isPersonalAccessToken(input)) {
        return {
          vendorPersonalAccessToken: input.vendorPersonalAccessToken,
        };
      }

      return {};
    },
    async getConfig() {
      try {
        return await lastValueFrom(
          this.client?.get(`/api/v2/notification?customerId=${this.customerId}&id=${this.thirdpartyConfigId}`) ?? EMPTY,
        );
      } catch (error) {
        throw new Error('Die Einstellungen der Generic Notification API Integration konnten nicht geladen werden');
      }
    },
    async edit(input: EditGenericNotificationApiInput) {
      const payload = {
        customerId: this.customerId,
        vendorId: this.config.vendorId ? parseInt(this.config.vendorId) : undefined,
        thirdpartyConfigId: parseInt(this.thirdpartyConfigId),
        label: input.label,
        consent: input.consent,
        vendorEndpoint: input.vendorEndpoint,
        authenticationType: input.authenticationType,
        treeNodeIds: input.treeNodeIds,
        heatingSystem: input.heatingSystem,
        ...this.getAuthenticationPayload(input),
      };
      try {
        await lastValueFrom(
          this.client?.put('/api/v2/notification', {
            ...payload,
          }) ?? EMPTY,
        );
      } catch (error) {
        // Leave loading untouched: the skeleton would remount the form fields and discard user input.
        throw new Error(await this.getSaveErrorMessage(error, input.authenticationType));
      }

      this.loading = true;
      this.loadData();

      const text = `Die Einstellungen der Generic Notification API Integration wurden gespeichert`;
      this.ADD_TOAST_MESSAGES({ messages: [{ text }] });
    },
    async getSaveErrorMessage(
      error: unknown,
      authenticationType: GenericNotificationApiAuthenticationType | undefined,
    ): Promise<string> {
      const status = getRxjsHttpErrorStatus(error);
      const responseBody = await getRxjsHttpErrorResponseBody(error);
      return (
        getGenericNotificationApiSaveErrorMessage(status, responseBody, authenticationType) ||
        'Die Einstellungen der Generic Notification API Integration konnten nicht gespeichert werden'
      );
    },
    async remove() {
      if (!window.confirm('Die Generic Notification API Integration wurde entfernt')) {
        return;
      }

      this.loading = true;
      try {
        await lastValueFrom(
          this.client?.delete('/api/v2/notification', {
            thirdpartyConfigId: this.thirdpartyConfigId ? parseInt(this.thirdpartyConfigId) : undefined,
          }) ?? EMPTY,
        );
      } catch (error) {
        this.loading = false;
        throw new Error('Die Generic Notification API Integration konnte nicht entfernt werden');
      }

      this.loading = false;
      const text = `Die Generic Notification API Integration wurde entfernt`;
      this.ADD_TOAST_MESSAGES({ messages: [{ text }] });

      this.$router.push({ name: 'AppCustomer/Customer/VendorApis' });
    },
    pluckIds<T>(objects: { id: T }[] | null): T[] {
      return objects === null ? [] : objects.map(({ id }) => id);
    },
    isNonEmptyString(value: unknown): boolean {
      return isString(value) && value.length > 0;
    },
    isValidEndpoint(value: unknown): boolean {
      if (!this.isNonEmptyString(value)) {
        return false;
      }

      try {
        const url = new URL(value as string);
        return (url.protocol === 'http:' || url.protocol === 'https:') && Boolean(url.hostname);
      } catch {
        return false;
      }
    },
    filterTreeNodes(treeNode: TreeNodeQueryForGenericNotificationApi_treeNodes_items) {
      return !treeNode.name.toLowerCase().includes('temperatur');
    },
    async loadData() {
      try {
        const response = (await this.getConfig()) as EditGenericNotificationApiInput;
        const treeNodes: { data: TreeNodeQueryForGenericNotificationApi } = await this.$apollo.query({
          query: treeNodeQueryForGenericNotificationApi,
          variables: { ids: response.treeNodeIds },
        });

        this.config = {
          ...response,
          treeNodes: response.treeNodeIds ? treeNodes?.data.treeNodes : null,
        };
      } finally {
        this.loading = false;
      }
    },
  },
});
